import React, { useEffect, useId, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { SERVICE_TYPE_ICONS } from '@rover/components/src/formFields/ServiceTypeFilter/ServiceTypeFilter';
import KEYS from '@rover/constants/src/platform/keys.constants';
import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Box, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import TruncatedText from '@rover/react-lib/src/pages/search/SearchPage/components/TruncatedText';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';

import buildCrossVerticalSearchUrl from '../buildCrossVerticalSearchUrl';

export type ServiceSelectProps = {
  /** The specialty service the page rendering the selector searches for. */
  currentOption: ServiceTypeOption;
  /** Every service the selector offers, `currentOption` among them. */
  serviceTypeOptions: ServiceTypeOption[];
};

/*
 * The trigger and the options are raw elements rather than Kibble `Button`/`Link`: they
 * share one row layout across a button and an anchor, and Kibble's own interaction states
 * would have to be overridden rather than reused.
 */
const DropdownTrigger = styled.button<{ $isOpen: boolean }>`
  display: block;
  width: 100%;
  padding: ${({ theme }) => `${theme.space['2x']} ${theme.space['4x']}`};
  background-color: ${({ theme }) => theme.background.primary};
  border: ${({ theme, $isOpen }) =>
    `${theme.borderWidth.primary} solid ${
      $isOpen
        ? theme.colors.interactive_border.primary_active
        : theme.colors.interactive_border.primary_base
    }`};
  border-radius: ${({ theme }) => theme.radii.secondary};
  cursor: pointer;
  text-align: left;
`;

const optionRowStyles = css`
  display: block;
  width: 100%;
  padding: ${({ theme }) => theme.space['3x']};
  border: none;
  background-color: transparent;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
  transition: background-color 0.2s ease;

  /* An inset ring rather than an outline, since the menu clips its overflow and would
     cut the outline off the first and last options. */
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) =>
      `inset 0 0 0 ${theme.borderWidth.primary} ${theme.colors.border.input_focus}`};
  }
`;

const OptionLink = styled.a`
  ${optionRowStyles}

  &:hover {
    background-color: ${({ theme }) => theme.background.tertiary};
    text-decoration: none;
  }
`;

const CurrentOptionButton = styled.button`
  ${optionRowStyles}

  background-color: ${({ theme }) => theme.background.tertiary};
`;

const OptionRow = ({
  option,
  iconColor,
}: {
  option: ServiceTypeOption;
  iconColor: string;
}): JSX.Element => {
  const Icon = SERVICE_TYPE_ICONS[option.value];

  return (
    <Flex alignItems="center" gap="2x" width="100%">
      {Icon && (
        <Box flexShrink="0">
          <Icon width={24} height={24} fill={iconColor} />
        </Box>
      )}
      <Flex flexDirection="column" flex="1" minWidth="0">
        <TruncatedText size="100" fontWeight="semibold" textColor="primary" textAlign="left">
          {option.title}
        </TruncatedText>
        {option.subtitle && (
          <TruncatedText size="100" textColor="secondary" textAlign="left">
            {option.subtitle}
          </TruncatedText>
        )}
      </Flex>
    </Flex>
  );
};

/**
 * Every option other than the page's own service is a cross-vertical link: core services
 * go to core search, and the other specialty service goes to its own search page.
 *
 * This component is largely a recreation of the ServiceTypeSection component used
 * on the core search page, but special cased to handle the "only-navigation" functionality
 * here. While it's not ideal that this component is largely recreating some pretty standard
 * dropdown behavior, this is to support this "quick test" and is not intended to be a
 * long-term user experience.
 */
const ServiceSelect = ({ currentOption, serviceTypeOptions }: ServiceSelectProps): JSX.Element => {
  const theme = useTheme();
  const { locale } = useI18nContext();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLElement | null>>([]);
  // Focus can land on an option by click or `Tab` as well as by arrow key, so the
  // keyboard handler reads the focused index from here rather than assuming its own
  // last move put it there.
  const focusedIndexRef = useRef(0);

  const menuId = useId();
  const currentIndex = serviceTypeOptions.findIndex(
    (option) => option.value === currentOption.value
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current?.contains(event.target as Node)) return;

      setIsOpen(false);
    };

    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Moving focus into the menu on open is what makes the arrow keys, `Escape`, and the
  // focus-out close below reachable without a pointer.
  useEffect(() => {
    if (isOpen) {
      optionRefs.current[currentIndex]?.focus();
    }
  }, [isOpen, currentIndex]);

  const close = (): void => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const focusOptionAt = (index: number): void => {
    const boundedIndex = Math.min(Math.max(index, 0), serviceTypeOptions.length - 1);
    optionRefs.current[boundedIndex]?.focus();
  };

  const handleOptionsFocus = (event: React.FocusEvent<HTMLElement>): void => {
    focusedIndexRef.current = optionRefs.current.indexOf(event.target as HTMLElement);
  };

  const handleOptionsKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const focusedIndex = focusedIndexRef.current;

    switch (event.key) {
      case KEYS.ESCAPE:
        event.preventDefault();
        close();
        break;
      case KEYS.DOWN:
        event.preventDefault();
        focusOptionAt(focusedIndex + 1);
        break;
      case KEYS.UP:
        event.preventDefault();
        focusOptionAt(focusedIndex - 1);
        break;
      case KEYS.HOME:
        event.preventDefault();
        focusOptionAt(0);
        break;
      case KEYS.END:
        event.preventDefault();
        focusOptionAt(serviceTypeOptions.length - 1);
        break;
      case KEYS.SPACE: {
        const focusedOption = optionRefs.current[focusedIndex];

        // Anchors activate on `Enter` alone, so `Space` would otherwise scroll the page
        // out from under an open menu.
        if (focusedOption?.tagName === 'A') {
          event.preventDefault();
          focusedOption.click();
        }
        break;
      }
      default:
        break;
    }
  };

  const handleOptionsBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    const nextFocused = event.relatedTarget as Node | null;

    if (nextFocused && !containerRef.current?.contains(nextFocused)) {
      setIsOpen(false);
    }
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === KEYS.DOWN && !isOpen) {
      event.preventDefault();
      setIsOpen(true);
    }
  };

  const CurrentIcon = SERVICE_TYPE_ICONS[currentOption.value];
  const iconColor = theme.colors.brand.active_brand;

  return (
    <Box position="relative" ref={containerRef}>
      <DropdownTrigger
        ref={triggerRef}
        $isOpen={isOpen}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={isOpen}
        aria-controls={menuId}
        // DEV-104195: Translate
        aria-label={`Service type: ${currentOption.title}`}
      >
        <Flex alignItems="center" gap="2x" width="100%">
          {CurrentIcon && (
            <Box flexShrink="0">
              <CurrentIcon width={24} height={24} fill={iconColor} />
            </Box>
          )}
          <Flex flexDirection="column" flex="1" minWidth="0">
            <TruncatedText size="200" fontWeight="semibold" textColor="primary" textAlign="left">
              {currentOption.title}
            </TruncatedText>
            {currentOption.subtitle && (
              <TruncatedText size="100" textColor="secondary" textAlign="left">
                {currentOption.subtitle}
              </TruncatedText>
            )}
          </Flex>
          <Box flexShrink="0">
            <DropdownSmall width={24} height={24} fill={theme.colors.text.primary} />
          </Box>
        </Flex>
      </DropdownTrigger>

      {isOpen && (
        <Box
          id={menuId}
          webAs="ul"
          role="list"
          background="primary"
          webProps={{
            onKeyDown: handleOptionsKeyDown,
            onFocus: handleOptionsFocus,
            onBlur: handleOptionsBlur,
          }}
          sx={(dropdownTheme) => ({
            position: 'absolute',
            margin: 0,
            padding: 0,
            listStyle: 'none',
            top: `calc(100% + ${dropdownTheme.space['1x']})`,
            left: 0,
            width: '100%',
            minWidth: '280px',
            border: `${dropdownTheme.borderWidth.secondary} solid`,
            borderColor: 'border.secondary',
            borderRadius: 'secondary',
            boxShadow: 'elevation_mid',
            zIndex: ZIndex.DROPDOWN.toNumber(),
            overflow: 'hidden',
          })}
        >
          {serviceTypeOptions.map((option, index) => {
            const registerOption = (node: HTMLElement | null): void => {
              optionRefs.current[index] = node;
            };

            return (
              <Box key={option.value} webAs="li">
                {index === currentIndex ? (
                  <CurrentOptionButton
                    ref={registerOption}
                    type="button"
                    onClick={close}
                    aria-current="page"
                  >
                    <OptionRow option={option} iconColor={iconColor} />
                  </CurrentOptionButton>
                ) : (
                  <OptionLink
                    ref={registerOption}
                    href={buildCrossVerticalSearchUrl({ serviceTypeSlug: option.value, locale })}
                  >
                    <OptionRow option={option} iconColor={iconColor} />
                  </OptionLink>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ServiceSelect;
